import React, { useEffect, useRef, useState } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import logoLockup from "src/assets/landing-v2/logo-lockup.png"
import coinIcon from "src/assets/landing/icon-coin.png"
import mysteryIcon from "src/assets/landing/icon-mystery.png"
import redemptionIcon from "src/assets/landing/icon-redemption.png"
import TopUpModal from "src/components/chrome/TopUpModal"
import AirtagLanding, { openTopUp } from "src/pages/landing/v2/behaviour"
import {
    getLoggedIn,
    getAccessToken,
    doLogout,
    doResetLogoutState,
    getLogoutSuccess,
    doGetLoggedInUser,
} from "src/reducers/reducer_core"
import { doGetCart, getCartData } from "src/reducers/reducer_cart"
import { doGetReferralSummary, getReferralSummaryData } from "src/reducers/reducer_referral"

// The site header — the new-ui export's design, used on EVERY page.
//
// It replaces AccountNav (which now re-exports this), so the bar no longer
// changes shape when you navigate off the landing page. Two things the export
// did not ship had to be added, because AccountNav owned them and 21 pages
// depend on them: the account dropdown (My Profile / Logout) and the Login
// pill for signed-out visitors.
//
// It also OWNS the behaviour class. The export's interactions — burger, wallet
// dock, language pills, the wallet chip's top-up sheet — are all bound by
// data-* attribute through that
// class, so mounting it here is what makes them work on every page rather than
// only on the landing route. The class is written to no-op on elements it
// cannot find, so pages without a hero simply skip the scroll effects.
export default function SiteHeader() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation()
    const { pathname } = location
    const { t, i18n } = useTranslation()

    const loggedIn = useSelector(getLoggedIn)
    const accessToken = useSelector(getAccessToken)
    const logoutSuccess = useSelector(getLogoutSuccess)
    const cartData = useSelector(getCartData)

    const [menuOpen, setMenuOpen] = useState(false)
    const rootRef = useRef(null)

    const isLoggedIn = !!(loggedIn || accessToken)
    // Balance icons in the wallet dock. Height-driven so each PNG keeps its own
    // aspect ratio; 26px is the tallest that clears the dock's 18px divider rules.
    const dockIcon = { height: '26px', width: 'auto', flex: '0 0 auto', display: 'block' }
    const isMalay = (i18n.language || "en").toLowerCase().startsWith("ms")
    const wallet = loggedIn?.wallet || {}

    // coins_total = purchased balance + matured referral commission. The two are
    // stored separately (checkout spends referral first and reports the split),
    // but the wallet should show what the customer can actually spend.
    const coins = wallet.coins_total ?? ((wallet.credit_point || 0) + (wallet.referral_available || 0))
    const draws = wallet.treasure_point || 0
    const points = wallet.airtag_point || 0
    const cartCount = cartData?.summary?.total_items || cartData?.data?.length || 0

    // The login payload does NOT carry the referral code — auth only reads
    // referral_code on REGISTER, to credit whoever referred you. The customer's
    // own code comes from the referral summary endpoint, which is why this used
    // to fall back to the export's placeholder and rendered an empty pill once
    // that placeholder was removed.
    const referralSummary = useSelector(getReferralSummaryData)
    const referralCode =
        referralSummary?.referral_code || loggedIn?.referral_code || loggedIn?.referralCode || ""
    const walletTo = isLoggedIn ? "/credit-details" : "/login"
    // Signed in, Top-Up opens the top-up chooser (behaviour's [data-topup-open]); signed out it
    // is a plain link to the login page.
    const topUpProps = isLoggedIn ? { 'data-topup-open': '', onClick: (e) => e.preventDefault() } : {}

    // The wallet dock (balances + the refer-&-earn pill) normally slides in
    // under the header on scroll and stays stuck there. On the profile page it
    // is the wrong band to pin: it sits on top of the form and pushes the
    // fields down on a phone, while duplicating numbers the profile itself
    // shows. Opting out keeps the header row — and its account dropdown —
    // sticky, and drops only the dock. Attribute rather than unmounting the
    // node, because the behaviour class resolves [data-walletbar] once at
    // mount, so a node absent then would never dock again on other routes.
    const dockOff = pathname === "/profile"

    // On the Mystery Box and Redemption pages the dock shows only the one balance that
    // page spends (client feedback, Oct 2026): no coins, no other balance, no referral
    // pill. Done with an attribute + CSS rather than unmounting, for the same reason
    // as data-dock-off above (the behaviour class binds these nodes once at mount).
    const dockOnly = pathname.startsWith("/treasure-box")
        ? "draws"
        : pathname === "/redemption" || pathname.startsWith("/redemption/")
          ? "points"
          : null

    // Underline follows the route (product detail pages count as Products);
    // pages that are neither leave both links plain.
    const desktopNavLinks = [
        { to: "/", label: t('landing.nav.home'), active: pathname === "/" },
        { to: "/products", label: t('landing.nav.products'), active: pathname === "/products" || pathname.startsWith("/products/") },
    ]

    const mobileNavLinks = [
        { to: "/products", label: t('landing.nav.products') },
        { to: "/treasure-box", label: t('nav.mysteryDraw') },
        { to: "/redemption", label: t('nav.redemption') },
    ]

    useEffect(() => {
        if (accessToken) {
            dispatch(doGetLoggedInUser())
            dispatch(doGetCart())
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (logoutSuccess) {
            dispatch(doResetLogoutState())
            navigate("/login")
        }
    }, [logoutSuccess, dispatch, navigate])

    // close the dropdown when a menu item navigates
    useEffect(() => {
        setMenuOpen(false)
    }, [pathname])

    useEffect(() => {
        if (!menuOpen) return undefined
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setMenuOpen(false)
        }
        document.addEventListener("mousedown", onDown)
        return () => document.removeEventListener("mousedown", onDown)
    }, [menuOpen])

    useEffect(() => {
        if (isLoggedIn && !referralSummary) dispatch(doGetReferralSummary())
    }, [isLoggedIn]) // eslint-disable-line react-hooks/exhaustive-deps

    // Mount the export's behaviour once per page, from the chrome that owns the
    // data-* attributes it binds to.
    useEffect(() => {
        const app = new AirtagLanding()
        app.props = {}
        app.opts = {
            referralCode,
            onLang: (lang) => i18n.changeLanguage(lang === "BM" ? "ms" : "en"),
        }
        app.componentDidMount()
        return () => app.componentWillUnmount()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Back from Coin Packages / Top Up with Card lands here with `openTopUp` set: reopen the
    // "Top Up Your Wallet" chooser, then clear the flag so a refresh does not reopen it.
    // Runs after the behaviour mount above, which is what owns the chooser.
    useEffect(() => {
        if (!location.state || !location.state.openTopUp) return undefined
        const timer = setTimeout(() => {
            openTopUp()
            navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
        }, 60)
        return () => clearTimeout(timer)
    }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleLogout = () => {
        setMenuOpen(false)
        dispatch(doLogout())
    }

    return (
        <>
            <div className="atl2c" ref={rootRef} data-screen-label="Header" data-header="" style={{ position: 'sticky', top: '0', zIndex: '50', background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(18px)', borderBottom: '1px solid #ECEAF2', transition: 'box-shadow .3s ease, background-color .3s ease' }}>
                {/* Utility strip — phones only (<768px, the same width the burger appears).
                  Language and the wallet balance move up here so the main row can
                  hold logo + top-up + cart + login + burger on ONE line instead of
                  wrapping or clipping.

                  Its language buttons are React-controlled rather than
                  [data-lang-opt]: the behaviour class's paintLang() repaints every
                  such element with the light-theme colours, which are unreadable on
                  this purple bar. */}
                <div data-utility-strip="" style={{ display: 'none', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '0 18px', height: '40px', background: '#3C028D' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button type="button" onClick={() => i18n.changeLanguage('en')} style={{ font: `${isMalay ? 500 : 800} 12px 'Manrope'`, letterSpacing: '.06em', color: isMalay ? 'rgba(255,255,255,0.62)' : '#fff', background: 'none', padding: 0, cursor: 'pointer' }}>EN</button>
                    <span aria-hidden="true" style={{ width: '1px', height: '13px', background: 'rgba(255,255,255,0.35)' }}></span>
                    <button type="button" onClick={() => i18n.changeLanguage('ms')} style={{ font: `${isMalay ? 800 : 500} 12px 'Manrope'`, letterSpacing: '.06em', color: isMalay ? '#fff' : 'rgba(255,255,255,0.62)', background: 'none', padding: 0, cursor: 'pointer' }}>BM</button>
                  </div>
                  {/* The balance is information only (client feedback, Oct 2026); just the
                      Top-Up pill is a control. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', color: '#fff' }}>
                    <img src={coinIcon} alt="" style={{ height: '20px', width: 'auto', flex: '0 0 auto', display: 'block' }} />
                    <span style={{ font: '800 13px \'Sora\'', letterSpacing: '-0.01em', color: '#fff' }}>{coins}</span>
                    <span style={{ font: '600 12px \'Manrope\'', color: 'rgba(255,255,255,0.78)' }}>{t('landing.coinsUnit')}</span>
                    {/* The header's Top-Up chip is hidden below 768px, so without this
                        the phone layout had the balance but no labelled way to top up. */}
                    <Link to={walletTo} {...topUpProps} style={{ font: '800 12px \'Manrope\'', letterSpacing: '.1em', textTransform: 'uppercase', color: '#3C028D', background: '#fff', border: '1px solid #fff', borderRadius: '13px', padding: '5px 12px', marginLeft: '4px', boxShadow: '0 1px 4px rgba(0,0,0,0.18)', cursor: 'pointer' }}>Top-Up</Link>
                  </div>
                </div>
                <div data-header-inner="" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px clamp(20px,2.6vw,40px)', padding: 'clamp(14px,1.5vw,20px) clamp(18px,3.4vw,48px)', transition: 'padding .3s cubic-bezier(.22,.61,.36,1)' }}>
                  <Link to="/" style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                    <img src={logoLockup} alt="Airtag — Beyond Expectations" style={{ height: 'clamp(46px,4vw,58px)', width: 'auto', display: 'block' }} />
                  </Link>
                  <div data-desktopnav="" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px clamp(18px,2.2vw,30px)', font: '600 14px \'Manrope\'' }}>
                    {desktopNavLinks.map((l) => (
                      <Link key={l.to} to={l.to} style={{ color: l.active ? '#14122B' : '#6B6485', paddingBottom: '3px', borderBottom: `2px solid ${l.active ? '#3C028D' : 'transparent'}` }}>{l.label}</Link>
                    ))}
                  </div>
                  <div data-header-actions="" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '9px' }}>
                    <div data-lang="" style={{ display: 'flex', alignItems: 'center', gap: '2px', height: '44px', padding: '4px', background: '#F7F6F9', border: '1px solid #ECEAF2', borderRadius: '22px', boxSizing: 'border-box' }}>
                      <span data-lang-opt="EN" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '34px', padding: '0 12px', borderRadius: '17px', background: '#3C028D', font: '800 11px \'Manrope\'', letterSpacing: '.06em', color: '#fff', cursor: 'pointer' }}>EN</span>
                      <span data-lang-opt="BM" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '34px', padding: '0 12px', borderRadius: '17px', background: 'transparent', font: '800 11px \'Manrope\'', letterSpacing: '.06em', color: '#6B6485', cursor: 'pointer' }}>BM</span>
                    </div>
                    <div data-coinchip="" style={{ display: 'flex', alignItems: 'center', gap: '9px', height: '44px', padding: '0 5px 0 8px', background: '#FFF2E4', border: '1px solid #F7D3AE', borderRadius: '22px', boxSizing: 'border-box' }}>
                      <img src={coinIcon} alt="" style={{ height: '28px', width: 'auto', flex: '0 0 auto', display: 'block' }} />
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                        <span style={{ font: '800 15px \'Sora\'', letterSpacing: '-0.02em', color: '#14122B', lineHeight: '1.1' }}>{coins}</span>
                        <span data-chip-unit="" style={{ font: '600 10px \'Manrope\'', color: '#8A6A3E' }}>coins</span>
                      </div>
                      <Link to={walletTo} {...topUpProps} style={{ display: 'inline-flex', alignItems: 'center', height: '34px', padding: '0 13px', borderRadius: '17px', background: '#F98412', font: '800 10px \'Manrope\'', letterSpacing: '.1em', textTransform: 'uppercase', color: '#fff', whiteSpace: 'nowrap', cursor: 'pointer' }} className="hv3">Top-Up</Link>
                    </div>
                    {/* Home. The logo already links here, but on a phone the desktop
                        nav is replaced by the burger, so this is the only one-tap way
                        back to the landing page without opening the menu. */}
                    <Link to="/" aria-label={t('landing.nav.home')} title={t('landing.nav.home')} style={{ width: '44px', height: '44px', flex: '0 0 44px', border: '1px solid #E3E1E9', borderRadius: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="hv1">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#14122B" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5"></path><path d="M5.5 9.6V20h13V9.6"></path><path d="M9.8 20v-5.4h4.4V20"></path></svg>
                    </Link>
                    <Link to="/cart" aria-label={t('nav.cart')} style={{ position: 'relative', width: '44px', height: '44px', flex: '0 0 44px', border: '1px solid #E3E1E9', borderRadius: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#14122B" strokeWidth="1.9"><path d="M5 7h14l-1.2 12H6.2L5 7z"></path><path d="M9 7V5.5a3 3 0 016 0V7"></path></svg>
                      {cartCount > 0 && <span style={{ position: 'absolute', top: '-3px', right: '-3px', minWidth: '18px', height: '18px', padding: '0 5px', borderRadius: '9px', background: '#FF0555', color: '#fff', font: '800 10px \'Manrope\'', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{cartCount}</span>}
                    </Link>
                    {isLoggedIn ? (
                      <button type="button" onClick={() => setMenuOpen((o) => !o)} aria-label={t('nav.accountMenu')} aria-haspopup="true" aria-expanded={menuOpen} style={{ position: 'relative', width: '44px', height: '44px', flex: '0 0 44px', border: '1px solid #E3E1E9', borderRadius: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: menuOpen ? '#F2EBFF' : 'transparent', cursor: 'pointer' }} className="hv1">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#14122B" strokeWidth="1.9"><circle cx="12" cy="12" r="9"></circle><circle cx="12" cy="10" r="3"></circle><path d="M6.5 18a6 6 0 0 1 11 0"></path></svg>
                      </button>
                    ) : (
                      <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', height: '44px', padding: '0 22px', borderRadius: '22px', background: '#3C028D', font: '800 11px \'Manrope\'', letterSpacing: '.09em', color: '#fff', whiteSpace: 'nowrap' }} className="hv3">{t('nav.login')}</Link>
                    )}
                    {isLoggedIn && menuOpen && (
                      <div role="menu" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: '0', zIndex: '60', minWidth: '196px', padding: '8px', background: '#fff', border: '1px solid #ECEAF2', borderRadius: '16px', boxShadow: '0 22px 44px rgba(20,18,43,0.16)', display: 'flex', flexDirection: 'column' }}>
                        <Link to="/profile" role="menuitem" onClick={() => setMenuOpen(false)} style={{ padding: '10px 12px', borderRadius: '10px', font: '600 14px \'Manrope\'', color: '#14122B' }} className="hv1">{t('nav.myProfile')}</Link>
                        <div style={{ height: '1px', background: '#ECEAF2', margin: '6px 4px' }}></div>
                        <button type="button" role="menuitem" onClick={handleLogout} style={{ padding: '10px 12px', borderRadius: '10px', font: '600 14px \'Manrope\'', color: '#FF0555', textAlign: 'left', cursor: 'pointer' }} className="hv1">{t('nav.logout')}</button>
                      </div>
                    )}
                    <span data-burger="" style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', flex: '0 0 44px', border: '1px solid #E3E1E9', borderRadius: '22px', cursor: 'pointer' }} className="hv1" role="button" aria-label="Menu" aria-expanded="false">
                      <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span data-burger-line="" style={{ display: 'block', width: '17px', height: '2px', borderRadius: '1px', background: '#14122B', transition: 'transform .3s cubic-bezier(.22,.61,.36,1), opacity .2s ease' }}></span>
                        <span data-burger-line="" style={{ display: 'block', width: '17px', height: '2px', borderRadius: '1px', background: '#14122B', transition: 'transform .3s cubic-bezier(.22,.61,.36,1), opacity .2s ease' }}></span>
                        <span data-burger-line="" style={{ display: 'block', width: '17px', height: '2px', borderRadius: '1px', background: '#14122B', transition: 'transform .3s cubic-bezier(.22,.61,.36,1), opacity .2s ease' }}></span>
                      </span>
                    </span>
                  </div>
                </div>
                <div data-mobilenav="" style={{ maxHeight: '0', opacity: '0', overflow: 'hidden', borderTop: '1px solid #ECEAF2', background: '#fff', transition: 'max-height .4s cubic-bezier(.22,.61,.36,1), opacity .3s ease' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', padding: '8px clamp(18px,3.4vw,48px) 16px' }}>
                    {/* No Home entry: the Home icon sits on the same row as the burger. */}
                    {mobileNavLinks.map((l, i) => (
                      <Link key={l.to} data-navlink="" to={l.to} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '52px', borderBottom: i < mobileNavLinks.length - 1 ? '1px solid #F2F1F4' : 'none', font: '700 16px \'Manrope\'', color: '#14122B' }}>{l.label}
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3C028D" strokeWidth="2.2"><path d="M9 6l6 6-6 6"></path></svg>
                      </Link>
                    ))}
                  </div>
                </div>
                <div data-walletbar="" {...(dockOff ? { 'data-dock-off': '' } : {})} {...(dockOnly ? { 'data-dock-only': dockOnly } : {})} style={{ maxHeight: '0', opacity: '0', overflow: 'hidden', background: '#FBFAFD', borderTop: '1px solid #ECEAF2', transition: 'max-height .45s cubic-bezier(.22,.61,.36,1), opacity .35s ease' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px clamp(18px,2.2vw,32px)', padding: '10px clamp(16px,3.4vw,48px)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px clamp(14px,1.6vw,22px)' }}>
                      <span data-dock-full="" data-dock-item="label" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', font: '700 10px \'Manrope\'', letterSpacing: '.18em', textTransform: 'uppercase', color: '#6B6485' }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3C028D" strokeWidth="2"><rect x="3" y="6" width="18" height="12" rx="2.5"></rect><path d="M16 12h3"></path></svg>{t('home.walletTitle')}
                      </span>
                      <div data-dock-coins="" data-dock-item="coins" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img src={coinIcon} alt="" style={dockIcon} />
                        <span style={{ font: '800 clamp(17px,1.5vw,21px) \'Sora\'', letterSpacing: '-0.03em', color: '#14122B' }}>{coins}</span>
                        <span style={{ font: '600 12px \'Manrope\'', color: '#6B6485' }}>{t('landing.airtagCoins')}</span>
                      </div>
                      <span data-dock-full="" data-dock-item="sep" style={{ width: '1px', height: '18px', background: '#E3E1E9' }}></span>
                      <div data-dock-item="draws" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img src={mysteryIcon} alt="" style={dockIcon} />
                        <span style={{ font: '800 clamp(17px,1.5vw,21px) \'Sora\'', letterSpacing: '-0.03em', color: '#14122B' }}>{draws}</span>
                        <span style={{ font: '600 12px \'Manrope\'', color: '#6B6485' }}>{t('landing.mysteryDraws')}</span>
                      </div>
                      <span data-dock-item="sep" style={{ width: '1px', height: '18px', background: '#E3E1E9' }}></span>
                      <div data-dock-item="points" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img src={redemptionIcon} alt="" style={dockIcon} />
                        <span style={{ font: '800 clamp(17px,1.5vw,21px) \'Sora\'', letterSpacing: '-0.03em', color: '#14122B' }}>{points}</span>
                        <span style={{ font: '600 12px \'Manrope\'', color: '#6B6485' }}>{t('landing.redemptionPoints')}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ height: '2px', background: 'transparent' }}><div data-progress="" style={{ width: '0%', height: '100%', background: 'linear-gradient(90deg,#3C028D,#F98412)' }}></div></div>
              </div>
            <TopUpModal />
        </>
    )
}
